import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface ITime {
  hours: number,
  minutes: number
}

@Injectable({
  providedIn: 'root'
})
export class ExamTimerService {
  private timerId: number | null = null;
  private TIMER_INTERVAL = 1000;
  private timerValue$: BehaviorSubject<number> = new BehaviorSubject(0);
  private startDate: Date;
  private totalDuration: number = 0;

  private set timerValue(value: number) {
    this.timerValue$.next(value);
  }

  private get timerValue(): number {
    return this.timerValue$.getValue();
  }

  get remainingTime(): Observable<ITime | undefined> {
    return this.timerValue$.pipe(map(miliseconds => miliseconds !== 0 ? this.getHoursAndMinutes(miliseconds) : undefined));
  }

  get remainingTimeMillis(): Observable<number> {
    return this.timerValue$.asObservable();
  }

  constructor(
    private readonly translateService: TranslateService
  ) {  }

  // duration in miliseconds
  start(duration: number, start = true): void {
    if (!!this.timerId) {
      clearInterval(this.timerId);
    }

    // keep track of when the test starts and how long the student can take
    this.totalDuration = duration;
    this.startDate = new Date();

    if (start) {
      this.timerId = window.setInterval(this.intervalCallback, this.TIMER_INTERVAL);
    }
  }

  private intervalCallback = () => {
    const now = new Date();
    const diff = now.valueOf() - this.startDate.valueOf();

    // diff is the amount of seconds that already passed
    // timerValue is the total amount of duration the test can take
    this.timerValue = this.totalDuration - diff;

    if (this.timerValue <= 0 && !!this.timerId) {
      this.stop();
    }
  }

  stop(): void {
    if (this.timerId) {
      clearInterval(this.timerId);
    }

    this.timerValue = 0;
  }

  extend(minutes: number): void {
    this.totalDuration = this.totalDuration + (minutes * 60 * 1000);
  }

  public getHoursAndMinutes(milliseconds: number): ITime {
    const asSeconds = milliseconds / 1000;

    let hours = 0;
    let minutes = Math.floor(asSeconds / 60);
    // let seconds = Math.floor(asSeconds % 60);

    if (minutes > 59) {
      hours = Math.floor(minutes / 60);
      minutes %= 60;
    }

    return { hours, minutes };
  }

  public splitTime(time: string): ITime {
    const split = time.split(':');
    return { hours: +split[0], minutes: +split[1] };
  }

  public convertTimeToMinutes(time: ITime) {
    return (time.hours * 60) + time.minutes;
  }

  public convertTimeToString(time: any): string {
    const hours: string = time?.hours?.toString() ?? '';
    const minutes: string = time?.minutes?.toString() ?? '';

    // if hours and minutes were empty, return empty string
    if (hours === '' && minutes === '') {
      return '';
    }

    // otherwise, return the time as string
    const h = hours === '' ? '00' : hours;
    const m = minutes === '' ? '00' : minutes.padStart(2, '0');
    return `${h}:${m}`;
  }

  public convertTimeToHMString(time: any): string {
    const hours: string = time?.hours?.toString() ?? '';
    const minutes: string = time?.minutes?.toString() ?? '';

    // if hours and minutes were empty, return empty string
    if (hours === '' && minutes === '') {
      return '';
    }

    // collect minute and hour letters, since they are different for each language
    const hourText = this.translateService.instant('EXAM_INFORMATION_DIALOG.TIME.HOUR_SHORTHAND');
    const minuteText = this.translateService.instant('EXAM_INFORMATION_DIALOG.TIME.MINUTE_SHORTHAND');

    // otherwise, return the time as string
    const h = (hours === '' ? '00' : hours) + hourText;
    const m = (minutes === '' ? '00' : minutes.padStart(2, '0')) + minuteText;
    return `${h} ${m}`;
  }

  convertMinutesToHMString(minutes: number): string {
    const time = this.getHoursAndMinutes(minutes * 60 * 1000); // milisecs
    return this.convertTimeToHMString(time);
  }
}
